// What the page has read of each session's title, for the rows of those it
// does not follow (format.ts, "titles"): the agent's own title and the first
// prompt, from each transcript the page followed, and the prompt of each
// session started from this page. Kept for the page's life, in memory: no
// transcript is read for a row alone.
import { useEffect, useSyncExternalStore } from 'react'
import type { SessionInfo } from '../sessions/info'
import type { KnownTitle } from './format'

let known: ReadonlyMap<string, KnownTitle> = new Map()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const read = () => known

/** Note what is known of a session's title; what it does not say stays as it was. */
export function noteTitle(session: string, said: Partial<KnownTitle>): void {
  const before = known.get(session) ?? { agent: null, prompt: null }
  const after: KnownTitle = {
    agent: said.agent === undefined ? before.agent : said.agent,
    prompt: before.prompt ?? said.prompt ?? null,
  }
  if (after.agent === before.agent && after.prompt === before.prompt && known.has(session)) return
  known = new Map(known).set(session, after)
  for (const listener of listeners) listener()
}

/** Forget them all: for the tests. */
export function forgetTitles(): void {
  known = new Map()
  for (const listener of listeners) listener()
}

/** What is known of every session's title. */
export function useKnownTitles(): ReadonlyMap<string, KnownTitle> {
  return useSyncExternalStore(subscribe, read)
}

/** Note what a followed transcript says of its session's title, as it says it. */
export function useNoteTitle(session: string, info: SessionInfo): void {
  useEffect(() => {
    if (info.title !== null || info.firstPrompt !== null) noteTitle(session, { agent: info.title, prompt: info.firstPrompt })
  }, [session, info.title, info.firstPrompt])
}
